import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../../supabase";
import { getOrCreateConversation } from "../../services/chat";
import { useAuth } from "../../contexts/AuthContext";
import { fetchSponsoredEmployerIds } from "../../services/listingPurchases";
import { getPillColor, getPillTextColor } from "../../components/employer/dashboard/constants";

const EmployerCandidates = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const jobId = searchParams.get('jobId');
    const [selectedTab, setSelectedTab] = useState("All Candidates");
    const [currentJobPage, setCurrentJobPage] = useState(1);
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedJob, setSelectedJob] = useState(null);
    const [jobCards, setJobCards] = useState([]);
    const [jobApplicationsCount, setJobApplicationsCount] = useState({});
    const [applications, setApplications] = useState([]);
    const [statusCounts, setStatusCounts] = useState({
        'applied': 0,
        'screening': 0,
        'interview': 0,
        'assessment': 0,
        'offer': 0,
        'hired': 0,
        'rejected': 0
    });
    const [isEmployerSponsored, setIsEmployerSponsored] = useState(false);

    // Calculate tab counts based on filtered candidates (after search filter but before tab filter)
    const getTabCount = (tabName) => {
        // Defensive check: ensure candidates is an array
        if (!Array.isArray(candidates) || candidates.length === 0) {
            return 0;
        }
        
        // Apply search filter first
        const searchFiltered = candidates.filter(candidate => {
            if (!candidate) return false;
            const matchesSearch = (candidate.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (candidate.role || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (candidate.location || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (Array.isArray(candidate.tags) ? candidate.tags : []).some(tag => (tag || '').toLowerCase().includes(searchTerm.toLowerCase()));
            return matchesSearch;
        });
        
        if (tabName === "All Candidates") {
            // When jobId is present, exclude rejected candidates from count
            if (jobId) {
                return searchFiltered.filter(c => {
                    const status = c.applicationStatus?.toLowerCase();
                    return status;
                }).length;
            }
            return searchFiltered.length;
        }
        if (tabName === "Recommended Matches") {
            return searchFiltered.filter(c => {
                if (!c || !c.matchPercentage) return false;
                const matchPct = parseInt(c.matchPercentage);
                return !isNaN(matchPct) && matchPct >= 80;
            }).length;
        }
        
        // For status-based tabs when jobId is present, only count candidates with applications for this job
        if (jobId) {
            if (tabName === "Application") {
                return searchFiltered.filter(c => c.applicationStatus?.toLowerCase() === 'applied').length;
            }
            if (tabName === "Qualified") {
                return searchFiltered.filter(c => c.applicationStatus?.toLowerCase() === 'screening').length;
            }
            if (tabName === "Interview") {
                return searchFiltered.filter(c => c.applicationStatus?.toLowerCase() === 'interview').length;
            }
            if (tabName === "Offer") {
                return searchFiltered.filter(c => c.applicationStatus?.toLowerCase() === 'offer').length;
            }
            if (tabName === "Hired") {
                return searchFiltered.filter(c => c.applicationStatus?.toLowerCase() === 'hired').length;
            }
            if (tabName === "Disqualified") {
                return searchFiltered.filter(c => c.applicationStatus?.toLowerCase() === 'rejected').length;
            }
        }
        
        // If no jobId, status-based tabs don't apply
        return 0;
    };

    // Dynamic tabs based on actual candidate data and application status
    const tabs = [
        { name: "All Candidates", count: getTabCount("All Candidates"), active: selectedTab === "All Candidates" },
        { name: "Recommended Matches", count: getTabCount("Recommended Matches"), active: selectedTab === "Recommended Matches" },
        { name: "Application", count: getTabCount("Application"), active: selectedTab === "Application", status: 'applied' },
        { name: "Qualified", count: getTabCount("Qualified"), active: selectedTab === "Qualified", status: 'screening' },
        { name: "Interview", count: getTabCount("Interview"), active: selectedTab === "Interview", status: 'interview' },
        { name: "Offer", count: getTabCount("Offer"), active: selectedTab === "Offer", status: 'offer' },
        { name: "Hired", count: getTabCount("Hired"), active: selectedTab === "Hired", status: 'hired' },
        { name: "Disqualified", count: getTabCount("Disqualified"), active: selectedTab === "Disqualified", status: 'rejected' }
    ];

    // Fetch job details if jobId is provided
    useEffect(() => {
        if (jobId) {
            fetchJobDetails();
        }
    }, [jobId]);

    // Fetch candidates data
    useEffect(() => {
        fetchCandidates();
    }, [jobId]);

    // Reset applications when jobId changes or is cleared
    useEffect(() => {
        if (!jobId) {
            setApplications([]);
            setStatusCounts({
                'applied': 0,
                'screening': 0,
                'interview': 0,
                'assessment': 0,
                'offer': 0,
                'hired': 0,
                'rejected': 0
            });
        }
        // Applications are fetched in filterCandidatesByJob when candidates are loaded
    }, [jobId]);

    const fetchApplications = async () => {
        if (!jobId || !user?.id) return;
        
        try {
            const { data, error } = await supabase
                .from('applications')
                .select('id, user_id, status, stage')
                .eq('job_listing_id', jobId);
            
            if (error) {
                return;
            }
            
            setApplications(data || []);
            
            // Count applications by status
            const counts = {
                'applied': 0,
                'screening': 0,
                'interview': 0,
                'assessment': 0,
                'offer': 0,
                'hired': 0,
                'rejected': 0
            };
            
            (data || []).forEach(app => {
                const status = app.status || 'applied';
                if (counts.hasOwnProperty(status)) {
                    counts[status]++;
                }
            });
            
            setStatusCounts(counts);
        } catch (err) {
            // Error fetching applications
        }
    };

    // Fetch employer's jobs
    useEffect(() => {
        fetchEmployerJobs();
    }, [user]);

    // Check if employer has sponsored listings
    useEffect(() => {
        const checkSponsoredStatus = async () => {
            if (!user?.id) return;
            try {
                const sponsoredIds = await fetchSponsoredEmployerIds();
                setIsEmployerSponsored(sponsoredIds.includes(user.id));
            } catch (err) {
                // Error checking sponsored status
            }
        };
        checkSponsoredStatus();
    }, [user]);

    const fetchJobDetails = async () => {
        try {
            const { data, error } = await supabase
                .from('internal_job_listings')
                .select('*')
                .eq('id', jobId)
                .eq('employer_id', user.id)
                .single();

            if (error) throw error;
            setSelectedJob(data);
        } catch (err) {
            // Error fetching job details
        }
    };

    const fetchEmployerJobs = async () => {
        if (!user) {
            return;
        }
        
        try {
            const { data, error } = await supabase
                .from('internal_job_listings')
                .select('*')
                .eq('employer_id', user.id)
                .neq('status', 'closed') // Exclude archived jobs
                .order('created_at', { ascending: false })
                .limit(10);

            if (error) throw error;
            
            // Sort by created_at ascending to determine which is the first (free) listing
            const sortedByCreation = [...data].sort((a, b) => 
                new Date(a.created_at) - new Date(b.created_at)
            );
            const firstJobId = sortedByCreation.length > 0 ? sortedByCreation[0].id : null;
            
            // Transform the data to match the expected format
            // Mark isSponsored: false for the first created job (free listing), true for the rest
            const transformedJobs = data.map(job => ({
                id: job.id,
                title: job.title,
                location: job.location,
                candidateCount: 0,
                applications: 0,
                isSponsored: job.id !== firstJobId // First job is free, rest are sponsored
            }));
            
            setJobCards(transformedJobs);

            // Fetch applications for these jobs to compute counts
            // Use simple counting logic matching Dashboard.jsx pattern
            const jobIds = data.map(j => j.id);
            if (jobIds.length > 0) {
                const { data: apps, error: appsError } = await supabase
                    .from('applications')
                    .select('id, job_listing_id, user_id, status')
                    .in('job_listing_id', jobIds);

                if (!appsError && apps) {
                    // Exclude only null status applications (include rejected in count)
                    const validApps = apps.filter(app => {
                        const status = app.status?.toLowerCase();
                        return status; // Include all applications with a status, including rejected
                    });
                    
                    // Count all applications per job (including rejected)
                    const counts = validApps.reduce((acc, app) => {
                        acc[app.job_listing_id] = (acc[app.job_listing_id] || 0) + 1;
                        return acc;
                    }, {});
                    setJobApplicationsCount(counts);
                } else if (appsError) {
                    setJobApplicationsCount({});
                }
            } else {
                setJobApplicationsCount({});
            }
        } catch (err) {
            setJobCards([]);
        }
    };

    // Compute candidate matches for each job using simple matching logic
    const computeMatchScore = (job, candidate) => {
        let score = 0;
        let total = 0;

        // Specialty/role match (40%)
        total += 40;
        const candidateRoles = candidate.profileData?.selectedRoles || [];
        if (job.specialty && candidateRoles.length > 0) {
            const match = candidateRoles.some(r =>
                String(r).toLowerCase() === String(job.specialty).toLowerCase()
            );
            if (match) score += 40;
        } else if (candidate.role && job.title) {
            // fallback on role vs title contains
            if (String(job.title).toLowerCase().includes(String(candidate.role).toLowerCase())) {
                score += 25; // partial
            }
        }

        // Location match (30%)
        if (job.location && candidate.location) {
            total += 30;
            if (String(candidate.location).toLowerCase().includes(String(job.location).toLowerCase())) {
                score += 30;
            }
        }

        // Employment type match (20%)
        const empTypes = candidate.profileData?.employmentTypes || [];
        if (job.employment_type && empTypes.length > 0) {
            total += 20;
            const matchEmp = empTypes
                .map(String)
                .map(v => v.toLowerCase())
                .includes(String(job.employment_type).toLowerCase());
            if (matchEmp) score += 20;
        }

        // Work preference presence bonus (10%) if available on job
        if (job.work_preference) {
            total += 10;
            score += 10;
        }

        const percent = total > 0 ? Math.round((score / total) * 100) : 0;
        return percent;
    };

    // Enrich job cards with dynamic candidate match counts and applications
    useEffect(() => {
        if (!jobCards || jobCards.length === 0) return;

        const enrich = async () => {
            try {
                // Fetch minimal job fields needed for match calc
                const ids = jobCards.map(j => j.id);
                const { data: jobsFull } = await supabase
                    .from('internal_job_listings')
                    .select('id, title, location, specialty, employment_type, work_preference')
                    .in('id', ids);

                const jobById = (jobsFull || []).reduce((acc, j) => { acc[j.id] = j; return acc; }, {});

                const updated = jobCards.map(card => {
                    const jf = jobById[card.id] || {};
                    // Count candidates meeting threshold (>=80 aligns with Recommended Matches tab)
                    const matchCount = (Array.isArray(candidates) ? candidates : []).reduce((cnt, cand) => {
                        if (!cand) return cnt;
                        const pct = computeMatchScore(jf, cand);
                        return cnt + (pct >= 80 ? 1 : 0);
                    }, 0);
                    return {
                        ...card,
                        candidateCount: matchCount,
                        applications: jobApplicationsCount[card.id] || 0
                    };
                });

                setJobCards(updated);
            } catch (e) {
                // Fallback: still set applications if available
                setJobCards(prev => prev.map(card => ({
                    ...card,
                    applications: jobApplicationsCount[card.id] || 0
                })));
            }
        };

        enrich();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidates, jobApplicationsCount]);

    const filterCandidatesByJob = async (candidates) => {
        if (!jobId) return candidates;

        try {
            // Convert jobId to string for consistent comparison
            const jobIdStr = String(jobId);
            
            // Get applications for this specific job from unified table
            const { data: applicationsData, error } = await supabase
                .from('applications')
                .select('id, user_id, status, stage')
                .eq('job_listing_id', jobIdStr);

            if (error) {
                throw error;
            }

            // Update applications state
            setApplications(applicationsData || []);
            
            // Filter out only null status applications (include rejected so they show in Disqualified tab)
            const validApps = (applicationsData || []).filter(app => {
                const status = app.status?.toLowerCase();
                return status; // Include all applications with a status, including rejected
            });
            
            // When a job is selected, fetch candidate profiles directly from applications
            // This ensures we show all applicants regardless of visibility settings (including rejected)
            const appliedUserIds = validApps.map(app => String(app.user_id));
            
            if (appliedUserIds.length === 0) {
                setStatusCounts({
                    'applied': 0, 'screening': 0, 'interview': 0, 'assessment': 0,
                    'offer': 0, 'hired': 0, 'rejected': 0
                });
                return [];
            }

            // Fetch candidate profiles for applicants (bypassing visibility filtering)
            const { data: applicantProfiles, error: profilesError } = await supabase
                .from('users_profiles')
                .select(`
                    profileid, role, email, firstname, lastname,
                    profession, location_city_country, profile_about,
                    profile_image_url, linkedinurl, jobsearchstatus,
                    skills, languages, work_experiences, educations,
                    selected_roles, employment_types,
                    hpcsa_verified, hpcsa_reg_no
                `)
                .in('profileid', appliedUserIds)
                .eq('role', 'Employee');

            if (profilesError || !applicantProfiles) {
                // Fallback to filtering from existing candidates if profile fetch fails
                const filteredCandidates = candidates.filter(candidate => {
                    const candidateIdStr = String(candidate.id);
                    return appliedUserIds.includes(candidateIdStr);
                });
                
                const candidatesWithStatus = filteredCandidates.map(candidate => {
                    const candidateIdStr = String(candidate.id);
                    const application = validApps.find(app => String(app.user_id) === candidateIdStr);
                    const status = application?.status || 'applied';
                    return {
                        ...candidate,
                        applicationStatus: status,
                        applicationId: application?.id || null
                    };
                });
                
                // Set status counts
                const counts = {
                    'applied': 0, 'screening': 0, 'interview': 0, 'assessment': 0,
                    'offer': 0, 'hired': 0, 'rejected': 0
                };
                candidatesWithStatus.forEach(candidate => {
                    const status = candidate.applicationStatus || 'applied';
                    if (counts.hasOwnProperty(status)) counts[status]++;
                });
                setStatusCounts(counts);
                
                return candidatesWithStatus;
            }

            // Build candidate objects from profiles
            const candidatesWithStatus = applicantProfiles.map((user) => {
                try {
                    const firstName = user.firstname || '';
                    const lastName = user.lastname || '';
                    const fullName = firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || 'Employee User';
                    
                    // Parse JSON fields
                    const skills = user.skills ? JSON.parse(user.skills) : [];
                    const languages = user.languages ? JSON.parse(user.languages) : [];
                    const workExperiences = user.work_experiences ? JSON.parse(user.work_experiences) : [];
                    const educations = user.educations ? JSON.parse(user.educations) : [];
                    const selectedRoles = user.selected_roles ? JSON.parse(user.selected_roles) : [];
                    
                    // Create tags from skills (limit to 3)
                    const tags = skills.slice(0, 3).map(skill => 
                        typeof skill === 'string' ? skill : skill.name
                    );
                    
                    // Get profile image URL
                    let profileImageUrl = null;
                    if (user.profile_image_url) {
                        if (user.profile_image_url.startsWith('http')) {
                            profileImageUrl = user.profile_image_url;
                        } else {
                            const { data: imageData } = supabase.storage
                                .from('profile-images')
                                .getPublicUrl(user.profile_image_url);
                            profileImageUrl = imageData.publicUrl;
                        }
                    }
                    
                    // Calculate match percentage
                    const completeness = calculateProfileCompleteness({
                        first_name: firstName,
                        last_name: lastName,
                        profession: user.profession,
                        location_city_country: user.location_city_country,
                        profile_about: user.profile_about,
                        skills: skills,
                        work_experiences: workExperiences,
                        educations: educations,
                        languages: languages,
                        linkedin_url: user.linkedinurl,
                        job_search_status: user.jobsearchstatus,
                        hpcsa_reg_no: user.hpcsa_reg_no
                    });
                    const matchPercentage = Math.min(100, Math.max(0, completeness));
                    
                    // Handle hpcsa_verified
                    const hpcsaRegNo = (user.hpcsa_reg_no || '').trim();
                    const value = user.hpcsa_verified;
                    const hpcsaVerified = value === true || value === 'true' || value === 1 || value === '1' || value === 't' || value === 'T';
                    
                    // Find application for this candidate (including rejected)
                    const candidateIdStr = String(user.profileid);
                    const application = validApps.find(app => String(app.user_id) === candidateIdStr);
                    const status = application?.status || 'applied';
                    
                    return {
                        id: user.profileid,
                        name: fullName,
                        role: user.profession || 'Medical Professional',
                        location: user.location_city_country || 'Location not specified',
                        matchPercentage: `${matchPercentage}%`,
                        matchLabel: "Profile Match",
                        tags: tags,
                        avatar: null,
                        profileImageUrl: profileImageUrl,
                        email: user.email || 'email@example.com',
                        aboutText: user.profile_about || 'Profile information not available',
                        skills: skills,
                        workExperiences: workExperiences,
                        educations: educations,
                        languages: languages,
                        linkedinUrl: user.linkedinurl || '',
                        jobSearchStatus: user.jobsearchstatus || '',
                        hpcsaRegNo: hpcsaRegNo,
                        hpcsaVerified: hpcsaVerified,
                        carLicense: false,
                        profileData: {
                            selectedRoles: selectedRoles,
                            employmentTypes: user.employment_types ? JSON.parse(user.employment_types) : []
                        },
                        applicationStatus: status,
                        applicationId: application?.id || null
                    };
                } catch (err) {
                    return null;
                }
            }).filter(candidate => candidate !== null);

            // Count applications by status
            const counts = {
                'applied': 0, 'screening': 0, 'interview': 0, 'assessment': 0,
                'offer': 0, 'hired': 0, 'rejected': 0
            };
            candidatesWithStatus.forEach(candidate => {
                const status = candidate.applicationStatus || 'applied';
                if (counts.hasOwnProperty(status)) counts[status]++;
            });
            setStatusCounts(counts);

            return candidatesWithStatus;
        } catch (err) {
            return candidates;
        }
    };

    const fetchCandidates = async () => {
        try {
            setLoading(true);
            
            // POPI COMPLIANCE: Only fetch candidates who applied to a specific job listing
            // If no jobId is provided, we cannot fetch candidates (will be handled by useEffect to redirect to latest job)
            if (!jobId || !user?.id) {
                setCandidates([]);
                return;
            }

            // Verify the job belongs to this employer
            const { data: jobData, error: jobError } = await supabase
                .from('internal_job_listings')
                .select('id, employer_id')
                .eq('id', jobId)
                .eq('employer_id', user.id)
                .single();

            if (jobError || !jobData) {
                setCandidates([]);
                return;
            }

            // Get applications for this specific job listing only
            const { data: applications, error: appsError } = await supabase
                .from('applications')
                .select('user_id, status, stage')
                .eq('job_listing_id', jobId);

            if (appsError) {
                throw appsError;
            }

            if (!applications || applications.length === 0) {
                setCandidates([]);
                return;
            }

            // Get unique user IDs from applications
            const candidateUserIds = [...new Set(applications.map(app => app.user_id))];

            // POPI COMPLIANCE: Only fetch profiles for candidates who applied to this job
            const { data: employeeUsers, error: usersError } = await supabase
                .from('users_profiles')
                .select(`
                    profileid, role, email, firstname, lastname,
                    profession, location_city_country, profile_about,
                    profile_image_url, linkedinurl, jobsearchstatus,
                    skills, languages, work_experiences, educations,
                    selected_roles, employment_types, profile_visibility,
                    hpcsa_verified, hpcsa_reg_no
                `)
                .eq('role', 'Employee')
                .in('profileid', candidateUserIds);

            if (usersError) {
                throw usersError;
            }

            if (!employeeUsers || employeeUsers.length === 0) {
                setCandidates([]);
                return;
            }

            // Create a map of application status by user_id for quick lookup
            const applicationStatusMap = new Map();
            applications.forEach(app => {
                applicationStatusMap.set(app.user_id, {
                    status: app.status || 'applied',
                    stage: app.stage || 'Applications'
                });
            });

            // Create candidate data from users_profiles (only for those who applied)
            const candidatesData = employeeUsers.map((user) => {
                try {
                    // Create candidate object from users_profiles data
                    const userEmail = user.email || 'email@example.com';
                    const firstName = user.firstname || '';
                    const lastName = user.lastname || '';
                    const fullName = firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || 'Employee User';
                    
                    // Parse JSON fields
                    const skills = user.skills ? JSON.parse(user.skills) : [];
                    const languages = user.languages ? JSON.parse(user.languages) : [];
                    const workExperiences = user.work_experiences ? JSON.parse(user.work_experiences) : [];
                    const educations = user.educations ? JSON.parse(user.educations) : [];
                    const selectedRoles = user.selected_roles ? JSON.parse(user.selected_roles) : [];
                    
                    // Create tags from skills (limit to 3)
                    const tags = skills.slice(0, 3).map(skill => 
                        typeof skill === 'string' ? skill : skill.name
                    );
                    
                    // Get profile image URL - check if it's already a full URL or just a path
                    let profileImageUrl = null;
                    if (user.profile_image_url) {
                        // If it's already a full URL (starts with http), use it directly
                        if (user.profile_image_url.startsWith('http')) {
                            profileImageUrl = user.profile_image_url;
                        } else {
                            // If it's just a path, generate the public URL
                            const { data: imageData } = supabase.storage
                                .from('profile-images')
                                .getPublicUrl(user.profile_image_url);
                            profileImageUrl = imageData.publicUrl;
                        }
                    }
                    
                    // Calculate match percentage based on profile completeness
                    const completeness = calculateProfileCompleteness({
                        first_name: firstName,
                        last_name: lastName,
                        profession: user.profession,
                        location_city_country: user.location_city_country,
                        profile_about: user.profile_about,
                        skills: skills,
                        work_experiences: workExperiences,
                        educations: educations,
                        languages: languages,
                        linkedin_url: user.linkedinurl,
                        job_search_status: user.jobsearchstatus,
                        hpcsa_reg_no: user.hpcsa_reg_no
                    });
                    const matchPercentage = Math.min(100, Math.max(0, completeness));
                    
                    // Handle hpcsa_verified - convert to boolean (more lenient check)
                    const hpcsaRegNo = (user.hpcsa_reg_no || '').trim();
                    const value = user.hpcsa_verified;
                    const hpcsaVerified = value === true || value === 'true' || value === 1 || value === '1' || value === 't' || value === 'T';
                    
                    // Get application status for this candidate
                    const applicationInfo = applicationStatusMap.get(user.profileid) || { status: 'applied', stage: 'Applications' };
                    
                    return {
                        id: user.profileid,
                        name: fullName,
                        role: user.profession || 'Medical Professional',
                        location: user.location_city_country || 'Location not specified',
                        matchPercentage: `${matchPercentage}%`,
                        matchLabel: "Profile Match",
                        tags: tags.length > 0 ? tags : ['Medical', 'Healthcare', 'Professional'],
                        avatar: null,
                        profileImageUrl: profileImageUrl,
                        email: userEmail,
                        aboutText: user.profile_about || 'Profile information not available',
                        skills: skills,
                        workExperiences: workExperiences,
                        educations: educations,
                        languages: languages,
                        linkedinUrl: user.linkedinurl || '',
                        jobSearchStatus: user.jobsearchstatus || '',
                        hpcsaRegNo: hpcsaRegNo,
                        hpcsaVerified: hpcsaVerified,
                        carLicense: false,
                        applicationStatus: applicationInfo.status,
                        applicationStage: applicationInfo.stage,
                        profileData: {
                            selectedRoles: selectedRoles,
                            employmentTypes: user.employment_types ? JSON.parse(user.employment_types) : []
                        }
                    };
                } catch (err) {
                    return null;
                }
            });

            // Filter out null results
            const validCandidates = candidatesData.filter(candidate => candidate !== null);
            
            // Apply profile visibility filtering (only for candidates who applied)
            const visibilityFilteredCandidates = validCandidates.filter(candidate => {
                const user = employeeUsers.find(u => u.profileid === candidate.id);
                if (!user) return false;
                
                const visibility = user.profile_visibility;
                
                // If visibility is 'hidden', hide from all employers
                if (visibility === 'hidden') {
                    return false;
                }
                
                // Since these candidates have already applied, they should be visible
                // (they've consented by applying)
                return true;
            });
            
            // Count applications by status
            const counts = {
                'applied': 0,
                'screening': 0,
                'interview': 0,
                'assessment': 0,
                'offer': 0,
                'hired': 0,
                'rejected': 0
            };
            
            visibilityFilteredCandidates.forEach(candidate => {
                const status = candidate.applicationStatus || 'applied';
                if (counts.hasOwnProperty(status)) {
                    counts[status]++;
                }
            });
            
            setStatusCounts(counts);
            setApplications(applications);
            setCandidates(visibilityFilteredCandidates);
        } catch (error) {
            setCandidates([]);
        } finally {
            setLoading(false);
        }
    };

    const calculateProfileCompleteness = (metadata) => {
        let score = 0;
        const checks = [
            // Basic info
            metadata.first_name && metadata.last_name,
            metadata.location_city_country || metadata.country,
            metadata.profile_about,
            
            // Skills and experience
            metadata.skills?.length > 0,
            metadata.work_experiences?.length > 0,
            metadata.educations?.length > 0,
            
            // Additional info
            metadata.languages?.length > 0,
            metadata.linkedin_url,
            metadata.job_search_status,
            metadata.hpcsa_reg_no
        ];
        
        const completedChecks = checks.filter(check => check).length;
        score = Math.round((completedChecks / checks.length) * 100);
        
        return Math.max(0, Math.min(100, score)); // Return actual score between 0-100%
    };

    const handleViewProfile = (candidateId) => {
        // Get jobId from URL if available
        const jobIdFromUrl = new URLSearchParams(window.location.search).get('jobId');
        if (jobIdFromUrl) {
            navigate(`/employer/candidate/${candidateId}?jobId=${jobIdFromUrl}`);
        } else {
            navigate(`/employer/candidate/${candidateId}`);
        }
    };

    const handleSendMessage = async (candidateId) => {
        if (!user?.id) return;
        try {
            const convo = await getOrCreateConversation(user.id, candidateId);
            navigate('/employer/messages', { state: { openConversationId: convo.id } });
        } catch (e) {
            // Failed to start conversation
        }
    };

    const filteredCandidates = (Array.isArray(candidates) ? candidates : []).filter(candidate => {
        if (!candidate) return false;
        
        // Apply search filter
        const matchesSearch = (candidate.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (candidate.role || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (candidate.location || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (Array.isArray(candidate.tags) ? candidate.tags : []).some(tag => (tag || '').toLowerCase().includes(searchTerm.toLowerCase()));
        
        if (!matchesSearch) return false;
        
        // Apply tab filter
        if (selectedTab === "Recommended Matches") {
            if (!candidate.matchPercentage) return false;
            const matchPct = parseInt(candidate.matchPercentage);
            return !isNaN(matchPct) && matchPct >= 80;
        }
        
        // If no jobId is selected, show all candidates for non-status tabs
        if (!jobId) {
            return selectedTab === "All Candidates";
        }
        
        // When jobId is present and viewing "All Candidates", exclude rejected candidates
        if (selectedTab === "All Candidates") {
            const status = candidate.applicationStatus?.toLowerCase();
            return status;
        }
        
        // Filter by application status for status-based tabs
        // Only show candidates who have an application for this job
        // Use case-insensitive comparison for consistency
        const candidateStatus = candidate.applicationStatus?.toLowerCase();
        if (selectedTab === "Application") {
            return candidateStatus === 'applied';
        }
        if (selectedTab === "Qualified") {
            return candidateStatus === 'screening';
        }
        if (selectedTab === "Interview") {
            return candidateStatus === 'interview';
        }
        if (selectedTab === "Offer") {
            return candidateStatus === 'offer';
        }
        if (selectedTab === "Hired") {
            return candidateStatus === 'hired';
        }
        if (selectedTab === "Disqualified") {
            return candidateStatus === 'rejected';
        }
        
        // For "All Candidates" tab, show all candidates
        return true;
    });

    return (
        <>
        <style>{`
            @media (max-width: 1024px) {
                .employer-candidates-header {
                    padding: 1rem !important;
                }
                .employer-candidates-header-inner {
                    flex-direction: column !important;
                    align-items: flex-start !important;
                    gap: 0.75rem !important;
                }
                .employer-candidates-header-inner button {
                    width: 100% !important;
                    justify-content: flex-start !important;
                }
                .employer-candidates-content {
                    padding: 1rem !important;
                }
                .employer-candidates-job-scroll {
                    gap: 0.75rem !important;
                }
                .employer-candidates-job-card {
                    width: 16rem !important;
                }
                .employer-candidates-carousel {
                    justify-content: space-between !important;
                }
                .employer-candidates-tabs {
                    gap: 0.5rem !important;
                }
                .employer-candidates-tabs button {
                    font-size: 0.875rem !important;
                }
                .employer-candidates-search input {
                    width: 100% !important;
                    font-size: 0.875rem !important;
                }
                .employer-candidates-grid {
                    grid-template-columns: 1fr !important;
                    gap: 0.75rem !important;
                }
                .employer-candidates-card {
                    padding: 1rem !important;
                }
                .employer-candidates-card-inner {
                    flex-direction: column !important;
                    align-items: stretch !important;
                    gap: 1rem !important;
                }
                .employer-candidates-card-actions {
                    margin-left: -40px !important;
                    width: 100% !important;
                    flex-direction: column !important;
                    align-items: stretch !important;
                    gap: 0.5rem !important;
                }
                .employer-candidates-card-actions button {
                    width: 100% !important;
                }
            }
        `}</style>
        <div className="min-h-screen bg-white employer-candidates-page">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 employer-candidates-header">
                <div className="flex items-center justify-between employer-candidates-header-inner">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">
                            {selectedJob ? `Candidates for ${selectedJob.title}` : 'Job Candidates'}
                        </h1>
                        {selectedJob && (
                            <div className="mt-2 text-sm text-gray-600">
                                <span className="font-medium">{selectedJob.company_name}</span> • {selectedJob.location}
                            </div>
                        )}
                    </div>
                    {selectedJob && (
                        <button
                            onClick={() => navigate('/employer/candidates')}
                            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                        >
                            ← Back to All Candidates
                        </button>
                    )}
                </div>
            </div>

            {/* Main Content */}
            <div className="px-6 py-6 employer-candidates-content">
                {/* Your Jobs Section */}
                <div className="mb-8">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Your jobs</h2>
                    <div className="relative">
                        <div className="flex space-x-4 overflow-x-auto pb-4 employer-candidates-job-scroll">
                            {jobCards.map((job) => {
                                const isSelected = String(job.id) === jobId;
                                const targetUrl = isSelected 
                                    ? '/employer/candidates'
                                    : `/employer/candidates?jobId=${job.id}`;
                                return (
                                <div key={job.id} 
                                    onClick={() => navigate(targetUrl)}
                                    role="button"
                                    aria-pressed={isSelected}
                                    tabIndex={0}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(targetUrl); }}
                                    className={`flex-shrink-0 w-80 bg-white border rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow employer-candidates-job-card relative ${isSelected ? 'border-black' : 'border-gray-200'}`}>
                                    {isEmployerSponsored && job.isSponsored && (
                                        <span className="absolute top-3 right-2 bg-gray-900 text-white text-[9px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full">
                                            Sponsored
                                        </span>
                                    )}
                                    <div className="flex flex-col h-full">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2 pr-16">{job.title}</h3>
                                        <div className="flex items-center text-sm text-gray-600 mb-2">
                                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                                            </svg>
                                            {job.location}
                                        </div>
                                        <div className="flex items-center justify-between mt-auto">
                                            <span className="text-sm font-semibold text-gray-900">
                                                {job.candidateCount} Candidate matches
                                            </span>
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                +{job.applications} applications
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                        
                        {/* Carousel Navigation - Only show if there are multiple pages */}
                        {(() => {
                            const jobsPerPage = 5;
                            const totalPages = Math.ceil(jobCards.length / jobsPerPage);
                            
                            if (totalPages <= 1) {
                                return null;
                            }
                            
                            return (
                                <div className="flex items-center justify-end mt-4 space-x-2 employer-candidates-carousel">
                                    <button 
                                        onClick={() => setCurrentJobPage(Math.max(1, currentJobPage - 1))}
                                        disabled={currentJobPage === 1}
                                        className={`p-2 ${currentJobPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-gray-600'}`}
                                    >
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                                        </svg>
                                    </button>
                                    <div className="flex space-x-1">
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                            <button 
                                                key={page}
                                                onClick={() => setCurrentJobPage(page)}
                                                className={`w-6 h-6 rounded-full text-xs font-medium ${
                                                    currentJobPage === page ? 'bg-black text-white' : 'bg-white text-black border border-gray-300'
                                                }`}
                                            >
                                                {page}
                                            </button>
                                        ))}
                                    </div>
                                    <button 
                                        onClick={() => setCurrentJobPage(Math.min(totalPages, currentJobPage + 1))}
                                        disabled={currentJobPage === totalPages}
                                        className={`p-2 ${currentJobPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-gray-600'}`}
                                    >
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/>
                                        </svg>
                                    </button>
                                </div>
                            );
                        })()}
                    </div>
                </div>

                {/* Candidate Pipeline Tabs */}
                <div className="mb-6">
                    <div className="flex space-x-8 overflow-x-auto pb-2 employer-candidates-tabs">
                        {tabs.map((tab) => (
                            <button
                                key={tab.name}
                                onClick={() => setSelectedTab(tab.name)}
                                className={`flex items-center space-x-2 whitespace-nowrap pb-2 border-b-2 transition-colors ${
                                    tab.active
                                        ? 'text-gray-900 border-gray-900 font-semibold'
                                        : 'text-gray-500 border-transparent hover:text-gray-700'
                                }`}
                            >
                                <span>{tab.name}</span>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    tab.active
                                        ? 'bg-black text-white'
                                        : 'bg-gray-100 text-gray-700'
                                }`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Search Bar */}
                <div className="mb-4">
                    <div className="relative max-w-2xl employer-candidates-search">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        <input
                            type="text"
                            placeholder="Search Talent..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                        />
                    </div>
                </div>

                {/* Results Count */}
                <div className="mb-6">
                    <p className="text-sm text-gray-500">
                        {loading ? 'Loading candidates...' : `Showing ${filteredCandidates.length} of ${candidates.length} candidates`}
                    </p>
                </div>

                {/* Candidate Grid */}
                {loading ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 employer-candidates-grid">
                        {filteredCandidates.map((candidate) => (
                        <div key={candidate.id} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow employer-candidates-card">
                            <div className="flex items-start space-x-4">
                                {/* Avatar */}
                                <div className="flex-shrink-0">
                                    <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                                        {candidate.profileImageUrl ? (
                                            <img 
                                                src={candidate.profileImageUrl} 
                                                alt={candidate.name} 
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <span className="text-lg font-medium text-gray-500">
                                                {candidate.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between employer-candidates-card-inner">
                                        <div className="flex-1">
                                            {/* Name with Verification Tick */}
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="text-lg font-semibold text-gray-900">
                                                    {candidate.name}
                                                </h3>
                                                {candidate.hpcsaRegNo && typeof candidate.hpcsaRegNo === 'string' && candidate.hpcsaRegNo.trim().length > 0 && (
                                                    <img
                                                        src="/SealCheck.svg"
                                                        alt={candidate.hpcsaVerified ? "HPCSA Verified" : "HPCSA Pending Verification"}
                                                        className={`w-5 h-5 transition-all ${
                                                            candidate.hpcsaVerified 
                                                                ? '' 
                                                                : 'grayscale opacity-50'
                                                        }`}
                                                        style={{
                                                            filter: candidate.hpcsaVerified 
                                                                ? 'none' 
                                                                : 'grayscale(100%) opacity(0.5)'
                                                        }}
                                                        title={candidate.hpcsaVerified ? "HPCSA Verified" : "HPCSA Verification Pending"}
                                                    />
                                                )}
                                            </div>
                                            
                                            {/* Role */}
                                            <p className="text-sm text-gray-900 mb-1">
                                                {candidate.role}
                                            </p>
                                            
                                            {/* Location */}
                                            <p className="text-sm text-gray-600 mb-3">
                                                {candidate.location}
                                            </p>

                                            {/* Tags */}
                                            <div className="flex flex-wrap items-center gap-2 mb-4">
                                                {candidate.tags.map((tag, index) => (
                                                    <span key={index} className={`inline-flex items-center px-3 py-1 rounded-full text-xs ${getPillColor[index]} ${getPillTextColor[index]}`}>
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="flex-shrink-0 ml-4 flex flex-col space-y-2 employer-candidates-card-actions">
                                            <button 
                                                onClick={() => handleViewProfile(candidate.id)}
                                                className="bg-black text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors"
                                            >
                                                View Profile
                                            </button>
                                            <button 
                                                onClick={() => handleSendMessage(candidate.id)}
                                                className="bg-white text-black border border-gray-300 px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-50 transition-colors">
                                                Send message
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        ))}
                    </div>
                )}

                {/* Load More Button - TODO: Implement pagination
                <div className="mt-8 text-center">
                    <button className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors">
                        Load More Candidates
                    </button>
                </div>
                */}
            </div>
        </div>
        </>
    );
};

export default EmployerCandidates;


